import * as React from 'react';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ErrorBoundaryState {
  error: Error | null;
  resetKey: number;
}

/**
 * Top-level error boundary. Catches render errors anywhere below it and
 * shows a friendly fallback with a Retry that bumps a key so the React
 * subtree fully remounts. Reload-the-page is the second-line-of-defense
 * if Retry doesn't recover (most often it won't, since the error came
 * back from the API or a stale cache).
 */
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { error: null, resetKey: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Surface in the browser console; in production we'd ship to a logger.
    // eslint-disable-next-line no-console
    console.error('UI error boundary caught:', error, info);
  }

  reset = (): void => {
    this.setState((s) => ({ error: null, resetKey: s.resetKey + 1 }));
  };

  reload = (): void => {
    window.location.reload();
  };

  render(): React.ReactNode {
    if (this.state.error) {
      return (
        <div className="flex h-full items-center justify-center bg-background p-6">
          <div className="max-w-md text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <AlertTriangle className="h-6 w-6" />
            </div>
            <h1 className="text-xl font-black tracking-tight">Something went wrong</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              The app hit an unexpected error. Try the action again, or reload the page if it keeps
              happening.
            </p>
            <pre className="mt-3 max-h-40 overflow-auto rounded-md bg-muted px-3 py-2 text-left text-[11px] text-muted-foreground">
              {this.state.error.message}
            </pre>
            <div className="mt-4 flex items-center justify-center gap-2">
              <Button variant="outline" onClick={this.reload}>
                <RotateCw />
                Reload
              </Button>
              <Button onClick={this.reset}>Try again</Button>
            </div>
          </div>
        </div>
      );
    }

    return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
  }
}
